import { useLocation } from 'react-router-dom';
import {
  Moon,
  Sun,
  Wifi,
  WifiOff,
  HelpCircle,
  Languages,
  ChevronRight,
  Menu,
  LogOut,
} from 'lucide-react';
import { useApp } from '@/store';
import { WorkerRole } from '@/data/types';
import { logout } from '@/lib/auth';
import { Theme, useTheme } from '@/lib/theme';
import { useT, LANGUAGES } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { UpdatesPanel } from '@/components/UpdatesPanel';

export function Header() {
  const loc = useLocation();
  const online = useApp(s => s.online);
  const startTour = useApp(s => s.startTour);
  const lang = useApp(s => s.lang);
  const setLang = useApp(s => s.setLang);
  const toggleSidebar = useApp(s => s.toggleSidebar);
  const companyName = useApp(s => s.settings.companyName);
  const currentUser = useApp(s => s.currentUser);
  const adminWorker = useApp(s =>
    s.workers.find(
      w =>
        w.active &&
        (w.role === WorkerRole.SUPERADMIN || w.role === WorkerRole.ADMIN)
    )
  );
  const displayName = adminWorker?.name ?? currentUser?.email ?? '';
  const initials = displayName
    ? displayName
        .split(/[\s@.]/)
        .filter(Boolean)
        .map(n => n[0])
        .join('')
        .slice(0, 2)
        .toUpperCase()
    : '?';
  const { theme, toggle } = useTheme();
  const t = useT();

  const crumbs = loc.pathname.replace(/^\//, '').split('/').filter(Boolean);
  const effective = crumbs.length ? crumbs : ['overview'];

  return (
    <header className="sticky top-0 z-20 flex h-14 items-center justify-between border-b bg-background/80 backdrop-blur-md px-3 sm:px-6">
      <div className="flex items-center gap-2 text-sm min-w-0">
        {/* Hamburger — visible below lg */}
        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          className="h-8 w-8 shrink-0 lg:hidden"
          aria-label="Toggle menu"
        >
          <Menu className="h-4 w-4" />
        </Button>

        <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-2.5 py-1 text-xs font-semibold text-primary">
          <span className="h-1.5 w-1.5 rounded-full bg-primary" />
          {companyName}
        </span>
        {effective.map((c, i) => (
          <span key={i} className="hidden sm:flex items-center gap-2">
            <ChevronRight className="h-3 w-3 text-muted-foreground/50" />
            <span
              className={cn(
                'text-xs',
                i === effective.length - 1
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground'
              )}
            >
              {c}
            </span>
          </span>
        ))}
        {/* Mobile: show only last crumb */}
        <span className="sm:hidden text-sm font-medium text-foreground truncate">
          {effective[effective.length - 1]}
        </span>
      </div>
      <div className="flex items-center gap-1 sm:gap-1.5 shrink-0">
        <div
          className={cn(
            'flex items-center gap-1 sm:gap-1.5 rounded-full px-2 sm:px-3 h-8',
            online
              ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-400'
              : 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
          )}
          data-tour="online-btn"
          aria-live="polite"
        >
          {online ? (
            <Wifi className="h-3.5 w-3.5" />
          ) : (
            <WifiOff className="h-3.5 w-3.5" />
          )}
          <span className="hidden sm:inline text-xs font-medium">
            {online ? t('header.online') : t('header.offline')}
          </span>
        </div>

        <UpdatesPanel />

        <Button
          variant="ghost"
          size="sm"
          onClick={startTour}
          className="gap-1.5 h-8 rounded-full px-2 sm:px-3 hover:bg-orange-50 hover:text-orange-700 dark:hover:bg-orange-950/40 dark:hover:text-orange-400"
          aria-label={t('header.tour')}
          data-tour="help-btn"
        >
          <HelpCircle className="h-3.5 w-3.5" />
          <span className="hidden sm:inline text-xs font-medium">
            {t('header.tour')}
          </span>
        </Button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="gap-1.5 h-8 rounded-full px-2 sm:px-3"
              aria-label={t('header.lang')}
              data-tour="lang-btn"
            >
              <Languages className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold uppercase tracking-wide">
                {lang}
              </span>
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            className="w-40 max-h-64 overflow-y-auto"
          >
            {LANGUAGES.map(l => (
              <DropdownMenuItem
                key={l.code}
                onClick={() => setLang(l.code)}
                className="justify-between"
              >
                {l.label}{' '}
                {lang === l.code && <span className="text-primary">✓</span>}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>

        <Button
          variant="ghost"
          size="icon"
          onClick={toggle}
          aria-label="Toggle theme"
          className="h-8 w-8 rounded-full"
        >
          {theme === Theme.DARK ? (
            <Sun className="h-3.5 w-3.5" />
          ) : (
            <Moon className="h-3.5 w-3.5" />
          )}
        </Button>

        {currentUser && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label={t('auth.signOut')}
                className="ml-1 sm:ml-2 flex items-center gap-2 rounded-full border bg-card/50 pr-2 sm:pr-3 pl-1 py-1 hover:bg-muted transition-colors"
                data-testid="user-menu-trigger"
              >
                <div className="flex h-6 w-6 items-center justify-center rounded-full flame-gradient text-[10px] font-bold text-white">
                  {initials}
                </div>
                <span className="hidden sm:inline text-xs font-medium max-w-[140px] truncate">
                  {displayName}
                </span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5 text-xs text-muted-foreground border-b mb-1">
                <div className="font-medium text-foreground truncate">
                  {currentUser.email}
                </div>
                {currentUser.roles?.length > 0 && (
                  <div className="mt-0.5 truncate">
                    {currentUser.roles
                      .filter(r => !r.startsWith('tenant:'))
                      .join(', ')}
                  </div>
                )}
              </div>
              <DropdownMenuItem
                onClick={() => {
                  // Hard reload after logout discards in-memory singletons
                  // (PouchDB handles, live sync feeds) so the next sign-in
                  // starts from a clean replica with no cross-tenant bleed.
                  void logout().finally(() => window.location.reload());
                }}
                className="text-destructive focus:text-destructive"
                data-testid="user-menu-signout"
              >
                <LogOut className="mr-2 h-4 w-4" />
                {t('auth.signOut')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
    </header>
  );
}
